import React from 'react';
import App from 'next/app';
import get from 'lodash/get';
import Head from 'next/head';
import styled from 'styled-components';
import * as Sentry from '@sentry/nextjs';
import { PageTransition } from 'next-page-transitions';
import nookies from 'nookies';
import Router from 'next/router';
import { theme, width } from '../styles/theme';
import { PeikkoContext, PeikkoProvider } from '../contexts/PeikkoContext';
import { AvatarProvider } from '../contexts/AvatarContext';
import Splash from '../components/Splash/Splash';
import Header from '../components/Header/Header';
import { SpinnerContainer } from '../components/Spinner';
import { WelcomingAvatar } from '../components/PageElements';
import { getProduct } from '../utils/helpers';
import { verifyUser } from '../apis/IdentifyApi';
import {
  setLocale,
  presetSchoolSsoCookies,
  isCookiesConsentGiven,
  checkTermsAccepted,
  getCookieDomain,
} from '../utils/cookie';
import env from '../utils/env';
import {
  GDPR_FUNCTIONAL_ENABLED,
  GDPR_STATISTICS_ENABLED,
  PREFERRED_LOGIN_VARIANT,
} from '../utils/constants';
import Analytics from '../utils/analytics';
import Error from './_error';
import { createUrl, getHost, isServerRender } from '../utils/url';
import { KEEP_SIGNED_IN_COOKIE_NAME } from '../utils/constants';
import {
  getToosaUrlByLocale,
  allowedLocales,
  getTitleByLocale,
} from '../utils/locale';
import { countries } from '../components/i18n';
import {
  setServerSideCookie,
  SCHOOL_COOKIE_NAME,
  SCHOOL_AUTH_MODEL_COOKIE_NAME,
  SCHOOL_PUPIL_LOGIN_COOKIE_NAME,
} from '../utils/cookie';
import { resolvePupilDoor } from '../utils/pupilDoor';
import { isPhoneFormRequest } from '../utils/phoneForm';
import { unsafeDecodeJwtPayload } from '../utils/jwt-unsafe';
import {
  isFederatedToken,
  isSchoolSsoToken,
  isSkolonToken,
} from '../utils/federated-login';
import {
  SKOLON_APP_RETURN_PATH,
  SKOLON_LOGIN_PATH,
  SKOLON_LOGIN_VARIANT,
  SKOLON_LOGOUT_RETURN_PATH,
} from '../utils/skolon';
import {
  SCHOOL_SSO_APP_RETURN_PATH,
  SCHOOL_SSO_LOGGED_OUT_COOKIE_NAME,
  SCHOOL_SSO_LOGIN_PATH,
  SCHOOL_SSO_LOGOUT_RETURN_PATH,
  getSchoolSsoLocaleCookie,
} from '../utils/schoolSso';
import { isKuoriShell } from '../utils/native-shell';
import '../styles/globals.css';

const { MAINTENANCE_MODE } = env;

const ComponentContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  margin: 0 auto;
`;

const AppContainer = styled.div`
  width: ${width.container}px;
  margin: 0 auto;
  background: ${theme.palette.white};
  height: 100%;
  overflow-y: auto;
  /*
   * In the full-screen st-kuori iOS WebView the content rendered under the
   * status bar / notch (login form, onboarding, header). env() resolves to 0
   * in ordinary browsers, desktop and Android Capacitor, so web/PWA users see
   * no change — only the iOS native shell gets the inset. Needs
   * viewport-fit=cover (set on the <meta> below). Mirrors st-toosa #502.
   */
  padding-top: env(safe-area-inset-top, 0px);

  @media (max-width: ${width.container - 1}px) {
    width: 100%;
  }
`;

function redirect(url, ctx?) {
  if (isServerRender(ctx)) {
    const { locale } = ctx.query;
    const { host } = ctx.req.headers;
    // Use createUrl (https for every real host; http only for localhost:3000).
    // TLS terminates at the load balancer, so ctx.req.connection.encrypted is
    // false in every deployed env — building the scheme from it emitted an
    // http:// Location that the native WebView (cleartext disabled) hard-refuses
    // with ERR_CLEARTEXT_NOT_PERMITTED. Browsers silently upgraded it; the shell
    // does not. https here also drops the redundant http→https hop on web.
    const parsedUrl = new URL(createUrl(host, url));
    const queryParams = new URLSearchParams(parsedUrl.search);
    const country = queryParams.get('country');

    // passing locale from toosa, but only if it's a normal locale, not a country
    if (locale && locale !== country) {
      parsedUrl.searchParams.set('locale', locale);
      ctx.res.writeHead(302, {
        Location: parsedUrl.href,
      });
    } else {
      ctx.res.writeHead(302, { Location: url });
    }
    ctx.res.end();
  } else if (url.startsWith('/')) {
    Router.push(url);
  } else {
    window.location = url;
  }
  return {};
}

export const getToosaRedirectUrl = ({
  token,
  user,
  locale,
  cookieFunctional,
  cookieStatistics,
  keepSignedIn,
}: {
  token: string;
  user: string | User;
  locale: string;
  cookieFunctional?: string | boolean | null;
  cookieStatistics?: string | boolean | null;
  keepSignedIn?: boolean;
}): string => {
  const userAsString = typeof user === 'string' ? user : JSON.stringify(user);
  const base64User =
    typeof window !== 'undefined'
      ? window.btoa(userAsString)
      : Buffer.from(userAsString).toString('base64');

  // Get Amplitude device ID (only if user has opted in)
  let amplitudeDeviceId: string | undefined;
  let amplitudeSessionId: string | undefined;
  try {
    if (cookieStatistics) {
      amplitudeDeviceId = Analytics.getDeviceId();
      amplitudeSessionId = Analytics.getSessionId();
    }
  } catch (error) {
    // Ad blocker or Amplitude not loaded - skip device ID transfer
    console.warn('Could not retrieve Amplitude device ID:', error);
  }

  const queryParams = new URLSearchParams({
    token,
    user: base64User,
    // Forward the consent flag whenever we have a definite value — including a
    // boolean `false` (explicit opt-out). The callers pass booleans, so a plain
    // truthiness check (`cookieFunctional && …`) silently dropped `false` and the
    // opt-out never reached toosa (breaks consent propagation on prod-FI, where
    // peikko and toosa have separate cookie jars). Exclude only null/undefined
    // (unknown) and '' (treated as unset).
    ...(cookieFunctional != null &&
      cookieFunctional !== '' && {
        cookieFunctional: String(cookieFunctional),
      }),
    ...(cookieStatistics != null &&
      cookieStatistics !== '' && {
        cookieStatistics: String(cookieStatistics),
      }),
    ...(amplitudeDeviceId && { amplitudeDeviceId }), // Add device ID
    ...(amplitudeSessionId && { amplitudeSessionId }),
    // Opted-in long-lived session (keep_signed_in companion cookie was set):
    // toosa's login handler turns this into its biometric-enroll prompt.
    ...(keepSignedIn && { keepSignedIn: '1' }),
  });

  return `${getToosaUrlByLocale(locale)}/login?${queryParams}`;
};

// NB. exported only for testing
export const getRedirect = async (
  ctx,
  locale,
  gdprVersion,
  token,
  user,
  onboarding,
  preferLoginVariant = 'phone',
  gdprConsentGiven,
  cookieFunctional,
  cookieStatistics,
  // UK student auth — read from the paired UK cookies set by /school.
  // Both default to undefined so non-UK users (no cookies) fall
  // through to the existing FI/SE redirect chain unchanged.
  schoolSlug?: string,
  schoolAuthModel?: string,
  // DEV-735 — the pupil door identify configured for this school, cached at
  // selector time. Undefined means no door is configured and none may be
  // substituted. Separate from schoolAuthModel on purpose: that one says how
  // a pupil comes to hold a voucher, this one how they prove who they are.
  schoolPupilLogin?: string,
): Promise<any> => {
  const unauthorizedPath = '/unauthorized';

  // whether user can directly open particular pages
  const noRedirectWithoutTokenPaths = [
    '/',
    '/email',
    unauthorizedPath,
    '/login',
    '/logout',
    '/expired-token',
    '/cross-origin-verification',
    '/locale',
    '/country',
    '/maintenance',
    '/cookies/consent',
    '/cookies/settings',
    // UK student auth — school selector + roster login + phrase pages
    // are entry points for UK pilot, visited before or independent of
    // locale/country/onboarding. Exempt from the standard redirect
    // logic. Spec §1.4 + §1.7 + §1.8 + §2.4.
    '/school',
    // School Chromebook icon's install URL (DEV-857): loads with no redirect
    // for anyone, outside maintenance mode.
    '/app/[slug]',
    '/login-roster',
    // UK teacher staff login (Microsoft). Pre-auth entry surface +
    // its own Auth0 hash callback — must render before auth, like
    // /school and /login-roster. Spec 2026-06-03-teacher-microsoft-login §4.
    '/login-staff',
    // Skolon tile landing + its Auth0 hash callback (DEV-677). Same shape
    // as /login-staff.
    '/login-skolon',
    // UK pupil sign-in with the school Microsoft account (DEV-736). Same
    // contract as /login-staff: pre-auth entry surface and its own Auth0
    // hash callback, so it must render before auth.
    '/login-pupil',
    // School Google sign-in callback and re-entry (DEV-794). Same shape as
    // /login-skolon.
    SCHOOL_SSO_LOGIN_PATH,
    '/phrase-setup',
    '/phrase-reset-required',
    '/phrase-forgotten',
  ];
  const noRedirectWithTokenPaths = [
    unauthorizedPath,
    '/login',
    '/logout',
    '/expired-token',
    '/maintenance',
  ];

  const defaultOutput = { pageProps: {} };

  if (MAINTENANCE_MODE === true && ctx.pathname !== '/maintenance') {
    return { ...defaultOutput, redirect: `/maintenance` };
  }

  if (ctx.pathname === '/maintenance') {
    return defaultOutput;
  }

  // UK student auth — the school selector is the FIRST page UK users
  // hit (they arrive from somebuddy.help → uk.somebuddy.com → /school).
  // Spec §1.4. They have no token, no locale cookie, no onboarding
  // cookie, no terms acceptance yet — so they fail every downstream
  // gate. Bypass the full redirect chain entirely; the selector flow
  // is self-contained.
  //
  // /login-roster is the same shape — pre-auth UK surface that needs
  // to render with only the somebuddy_school cookie set. The page
  // itself handles the "no cookie → redirect to /school" case.
  //
  // /login-staff is the teacher Microsoft staff login (Westminster
  // pilot). Same shape: pre-auth surface, AND it's the Auth0 hash
  // callback target (responseType token id_token), so the token only
  // exists in the URL fragment client-side — server-side getRedirect
  // must let it render untouched so the page can parseHash → setToken.
  //
  // /login-skolon (DEV-677) is the Skolon tile landing and callback. It
  // must also stay untouched AFTER parseHash sets the token: the page
  // activates the user before handing off to `/`, and verifyUser here
  // would 404 a first-time user before activation has run.
  if (
    ctx.pathname === '/school' ||
    // The school Chromebook icon's install URL (DEV-857). Google shows a
    // placeholder app if it can't load, so no redirect, token or not. The
    // maintenance check above still applies; Chrome retries the install later.
    ctx.pathname === '/app/[slug]' ||
    ctx.pathname === '/login-roster' ||
    ctx.pathname === '/login-staff' ||
    ctx.pathname === '/login-skolon' ||
    // /login-pupil has exactly the shape described above: pre-auth surface
    // AND its own Auth0 hash callback. Without it here, a pupil who still
    // holds a token — re-running the flow, or carrying a stale one — gets
    // sent through verifyUser and redirected away during SSR, before the
    // useEffect ever reads the hash, and the fresh id_token is discarded.
    ctx.pathname === '/login-pupil' ||
    // /login-school (DEV-794) — exactly /login-skolon's reasons: Auth0 hash
    // callback, and it activates the pupil before handing off, so
    // verifyUser here would 404 a first-time pupil.
    ctx.pathname === SCHOOL_SSO_LOGIN_PATH ||
    ctx.pathname === '/phrase-setup' ||
    ctx.pathname === '/phrase-reset-required' ||
    ctx.pathname === '/phrase-forgotten'
  ) {
    return defaultOutput;
  }

  // UK student auth — forced phrase change after DSL reset.
  // identify's /admin/dsl/reset-phrase (DEV-578) sets
  // app_metadata.phrase_reset_required: true on the user. Auth0's
  // post-login Action (trigger 2) stamps that into the id_token as
  // the namespaced custom claim
  // `https://somistech.fi/phrase_reset_required`. We read THAT claim
  // here (NOT raw app_metadata — Auth0 doesn't put app_metadata in
  // id_tokens by default; only namespaced custom claims set by an
  // Action are visible to clients). Caught 2026-06-02 when the JWT
  // decode for a test user returned undefined for the flag despite
  // app_metadata being set correctly.
  //
  // The /phrase-reset-required early-exit above prevents the
  // infinite-loop case (we don't redirect-to-where-we-are).
  // Malformed/decode-failing tokens fall through silently — they
  // were already invalid for other reasons that the standard
  // verifyUser path will catch.
  if (token) {
    const decoded = unsafeDecodeJwtPayload(token);
    const flag = decoded?.['https://somistech.fi/phrase_reset_required'];
    if (flag === true) {
      return { ...defaultOutput, redirect: '/phrase-reset-required' };
    }
  }

  // First-time activation signal — used by the verifyUser 404 handling
  // below. A user holding a token but with no subscription yet gets
  // identify's GET /user 404 mapped to /voucher, instead of falling through
  // to the FI/SE passwordless fallback. Two cases:
  //   - any UK user (en-GB): a teacher with no PROFESSIONAL voucher, or a
  //     student pre-activation. Found 2026-06-04 on the teacher Microsoft
  //     login.
  //   - any federated login (DEV-715), keyed on how the user authenticated
  //     rather than on locale: a sub that is not `sms|`/`email|` (Microsoft
  //     `waad|`, Skolon `oauth2|skolon|`, later Google). Without it a
  //     non-UK first-time SSO user stranded at /email.
  // FI/SE passwordless subs are neither, so their 404 behaviour is unchanged.
  const canRescueFirstLogin =
    (Boolean(token) && locale === 'en-GB') ||
    (Boolean(token) && isFederatedToken(token));

  // The school's pupil door, from the cookies /school wrote. Computed here,
  // ahead of the gauntlet, because the school-Google rules below need it
  // before /country, /terms and the rest (review 2026-10-23, S2/S5); what it
  // means for the doors is under "UK login surface" further down.
  // Shared with /email, which uses the same answer to decide whether the
  // phone-login link would just bounce the user back here. Keeping the two
  // in one function is deliberate: they were written against different
  // cookies once, and that produced a redirect loop.
  const pupilDoor = resolvePupilDoor(schoolPupilLogin, schoolAuthModel);

  // The phone form lives at '/', which is exactly where the doors below
  // intercept a UK pupil — so /email's "sign in with a phone number" link
  // was a round trip, and DEV-727 hid it rather than fix it. The pilot
  // wants every way in reachable, to find out which pupils actually use,
  // so the link is back and points at '/?login=phone'. This is the flag
  // that makes it arrive.
  //
  // Exactly 'phone', not any truthy value: a stray ?login= must not become
  // a skeleton key that disables every door.
  //
  // It is a routing convenience and not a control. /email is already
  // reachable directly, and what a pupil is entitled to is decided by the
  // voucher, not by which form they typed into. ⚠️ That is also the
  // deadline on this: when DEV-737 starts refusing a voucher whose school
  // disagrees with how the pupil signed in, a pupil who came this way at a
  // Microsoft school would be refused at activation. Revisit both this and
  // the email fallback on /login-pupil then.
  const askedForPhoneForm = isPhoneFormRequest(ctx.query);

  // Skolon tile users never see or type a voucher (DEV-677). Every branch
  // below that would send a user to /voucher sends a Skolon sub back to
  // /login-skolon?from=app instead, which re-runs the silent activation and
  // ends on a terminal screen if that can't succeed. /login-skolon returns
  // early above, so this can't loop inside getRedirect.
  const isSkolonLogin = Boolean(token) && isSkolonToken(token);
  // DEV-794 — same rule for a pupil who signed in through their school's own
  // connection (sub `oauth2|<connection>|`, connection on the allow-list, e.g.
  // `crosthwaite-google`): no voucher to type, so every /voucher branch goes
  // to /login-school?from=app, which activates silently or ends on a
  // terminal screen. /login-school returns early above, so no loop here.
  // On a google-door device an unlisted `oauth2|` connection counts too
  // (see isSchoolSsoSub).
  const isSchoolSsoLogin =
    Boolean(token) && isSchoolSsoToken(token, { pupilDoor });
  const activationPath = isSkolonLogin
    ? SKOLON_APP_RETURN_PATH
    : isSchoolSsoLogin
    ? SCHOOL_SSO_APP_RETURN_PATH
    : '/voucher';

  const countryLocales: string[] = countries.map((_) => _.shortname);

  if (noRedirectWithTokenPaths.includes(ctx.pathname)) {
    return defaultOutput;
  }

  // DEV-794. A pupil at a school-Google school whose session has gone
  // (toosa sent them back, logout, from=app, a 5xx, or the token expired)
  // goes back through SSO, which on their managed Chromebook is silent.
  // /login-school resolves the connection from the school cookie itself.
  //
  // AHEAD of the consent/locale/onboarding gauntlet, and with no locale
  // gate (review 2026-10-23, S2). A five-year-old cannot get through /country
  // once the locale cookie lapsed; /login-school writes locale and onboarding
  // itself once the pupil is activated. Terms and cookie consent follow after
  // that, with a token (DEV-846).
  //
  // Still behind the same two sticky variants it always was behind: a
  // Skolon account belongs on /login-skolon, and a returning teacher
  // (preferLoginVariant 'staff', from /login-staff) must be kept off the
  // pupil connection — /login-school clears the variant on a pupil's
  // activation, so a teacher's earlier sign-in on a shared device does not
  // keep the pupil out. An explicit ?login=phone skips it, like every door.
  //
  // After a logout (/logout left the marker cookie) it is the
  // tap-to-sign-in screen instead: auto-starting would sign a pupil whose
  // Google session is still live straight back in, so logout would look
  // broken. The marker, not the login variant, carries this because an
  // expired session must still auto-start; the next successful activation
  // clears it. See SCHOOL_SSO_LOGOUT_RETURN_PATH. Loops are bounded by
  // /login-school's own start and from=app budgets.
  if (
    !token &&
    ctx.pathname === '/' &&
    !askedForPhoneForm &&
    schoolSlug &&
    pupilDoor === 'google' &&
    preferLoginVariant !== SKOLON_LOGIN_VARIANT &&
    preferLoginVariant !== 'staff'
  ) {
    const loggedOut =
      nookies.get(ctx)[SCHOOL_SSO_LOGGED_OUT_COOKIE_NAME] === '1';
    return {
      ...defaultOutput,
      redirect: loggedOut
        ? SCHOOL_SSO_LOGOUT_RETURN_PATH
        : SCHOOL_SSO_LOGIN_PATH,
    };
  }

  // A school-SSO pupil holding a token skips country, locale and onboarding:
  // /login-school?from=app re-activates them and writes those before handing
  // back to `/` (S2). Its from=app budget ends a loop on "Ask your teacher" if
  // the cookies somehow don't stick. Terms and cookie consent are not skipped:
  // nobody may accept them for the pupil, so they use plain redirects (DEV-846).
  const gauntletRedirect = (path: string) => ({
    ...defaultOutput,
    redirect: isSchoolSsoLogin ? SCHOOL_SSO_APP_RETURN_PATH : path,
  });

  // if country set as locale (like 'FI') then we can skip /country
  if (ctx.pathname !== '/locale' && locale && countryLocales.includes(locale)) {
    return gauntletRedirect(`/locale?country=${locale}`);
  }

  // if no valid locale is found, then go to country and locale selection
  if (!locale || !allowedLocales.includes(locale)) {
    if (ctx.pathname === '/country' || ctx.pathname === '/locale') {
      return defaultOutput;
    }
    return gauntletRedirect('/country');
  }

  // if user wants to change a language from the menu
  if (ctx.pathname === '/country' || ctx.pathname === '/locale') {
    return defaultOutput;
  }

  const checkOnboardingCompleted = (onboardingCookieValue: string): boolean =>
    onboardingCookieValue === 'v1';

  // A Skolon account goes straight to terms and cookie consent: no onboarding
  // slides, signed in or back on the logged-out path to /login-skolon.
  const skipsOnboarding =
    isSkolonLogin || preferLoginVariant === SKOLON_LOGIN_VARIANT;

  // Tells /terms and /cookies/consent to count only their own two steps. Read
  // here because a page can't always see the token: toosa's is httpOnly.
  const noOnboardingSlides =
    skipsOnboarding ||
    isSchoolSsoLogin ||
    (pupilDoor === 'google' && preferLoginVariant !== 'staff');
  const consentScreenOutput = {
    ...defaultOutput,
    pageProps: noOnboardingSlides ? { noOnboardingSlides } : {},
  };

  // if onboarding is not completed yet
  if (!skipsOnboarding && !checkOnboardingCompleted(onboarding)) {
    if (ctx.pathname === '/onboarding/1' || ctx.pathname === '/onboarding/2') {
      return defaultOutput;
    }
    return gauntletRedirect('/onboarding/1');
  }

  // if terms is not accepted
  if (!checkTermsAccepted(gdprVersion)) {
    if (ctx.pathname === '/terms' || ctx.pathname === '/privacy') {
      return consentScreenOutput;
    }
    return { ...defaultOutput, redirect: '/terms' };
  }

  const isOnConsentPage =
    ctx.pathname === '/cookies/consent' || ctx.pathname === '/cookies/settings';

  if (!isCookiesConsentGiven(gdprConsentGiven)) {
    if (isOnConsentPage) {
      return consentScreenOutput;
    }
    return { ...defaultOutput, redirect: '/cookies/consent' };
  }

  const consentCookiesMissing =
    cookieFunctional === 'missing' || cookieStatistics === 'missing';
  if (consentCookiesMissing && !isOnConsentPage) {
    return { ...defaultOutput, redirect: '/cookies/consent' };
  }

  // UK login surface — the ONLY difference between UK and FI/SE pre-login
  // (product decision, Kaisa 2026-06-04): UK users complete the SAME
  // onboarding / terms / cookie-consent gauntlet ABOVE, then — instead of
  // the FI/SE passwordless pages (/ phone, /email) — a UK user (en-GB) is
  // sent to /school to choose their school + auth method. A returning user
  // who already picked a roster school (paired school cookies) skips
  // straight to /login-roster. Placed AFTER the gauntlet on purpose so the
  // pre-login screens stay identical to FI/SE. Gated on !token + pathname
  // '/' so it only swaps the login surface; authenticated users fall
  // through to the verifyUser decision below. A passwordless UK school goes
  // to /email.
  //
  // DEV-735 — which door a pupil gets now comes from identify's per-school,
  // per-role configuration, cached in the pupil-login cookie. auth_model is
  // no longer consulted for this: it says how a pupil comes to hold a
  // voucher, never how they sign in (identify docs/adr/0001).
  //
  // ⚠️ A school with no configured door does NOT get a "no door" screen. No
  // branch matches, execution leaves this block, and '/' renders the FI/SE
  // phone form — the same place voucher_self schools have always landed.
  // This PR does not regress that and does not fix it either; a proper
  // terminal screen ("your school isn't set up yet") is a product decision
  // and a separate change. Do not read the rule as enforced here: what is
  // enforced is only that we never *pick a door we were not told about*.
  //
  // Transitional fallback: everyone who picked a school before this shipped
  // has the slug and auth_model cookies but no pupil-login cookie. Without
  // the derivation below they would land on the FI/SE form immediately.
  // Note what this means when a school is later reconfigured: those pupils
  // keep deriving their OLD door until the cookie turns over or they revisit
  // /school, so a switch to Microsoft does not reach them. That is a grace
  // period rather than a bug — they keep the subject that holds their
  // history — but it is accidental, so it is written down in the
  // switching-a-school runbook rather than left to be rediscovered.
  // (pupilDoor itself is computed above, ahead of the gauntlet.)

  if (!token && ctx.pathname === '/') {
    // The doors, and only the doors, are what an explicit request for the
    // phone form skips. Picking a school is not part of what they asked
    // to skip, so the /school branch below stays in force — '/' is not
    // the school picker, and the UK flow still needs one.
    if (!askedForPhoneForm) {
      // Checked FIRST among the doors, and with no locale gate. A Skolon
      // account has no passphrase, no voucher and no school to pick, so
      // every other door is a dead end for it — a Swedish Skolon user would
      // get the sv-SE passphrase form, a UK one the /school picker. The
      // cookie is only ever written by a successful Skolon activation, so
      // it is a reliable signal on its own, and leaving it ungated keeps
      // the UK Skolon app working without a second branch.
      //
      // Inside the askedForPhoneForm gate like the others: someone who
      // explicitly asks for the phone form means it. /login-skolon's own
      // "sign in another way" link clears the cookie instead, which is the
      // durable escape; this query param is the one-off.
      if (preferLoginVariant === SKOLON_LOGIN_VARIANT) {
        return { ...defaultOutput, redirect: SKOLON_LOGOUT_RETURN_PATH };
      }
      // Roster stays ahead of the staff branch, where it has always been.
      // Reordering it would change where a teacher at a roster school
      // lands, which is a separate question (DEV-619) and not this
      // change's business.
      if (schoolSlug && pupilDoor === 'roster_phrase') {
        return { ...defaultOutput, redirect: '/login-roster' };
      }
      // Returning teacher: PREFERRED_LOGIN_VARIANT='staff' is set on
      // Microsoft sign-in (login-staff) and survives logout (which clears
      // only token+user) → send them straight back to /login-staff instead
      // of the student-facing /school picker.
      if (locale === 'en-GB' && preferLoginVariant === 'staff') {
        return { ...defaultOutput, redirect: '/login-staff' };
      }
      // Passwordless stays behind the staff branch, where DEV-727 put it: a
      // returning teacher at a passwordless school still belongs on
      // /login-staff. Still gated on schoolSlug, so a stale or incomplete
      // cookie set keeps falling through rather than reaching a door we
      // have not confirmed for that school.
      if (schoolSlug && locale === 'en-GB' && pupilDoor === 'passwordless') {
        return { ...defaultOutput, redirect: '/email' };
      }
      // DEV-736. Also behind the staff branch, and for a sharper reason
      // than the others: both doors go through the same Auth0 Microsoft
      // connection, so ordering is the only thing keeping a returning
      // teacher off the pupil surface.
      if (schoolSlug && locale === 'en-GB' && pupilDoor === 'microsoft') {
        return { ...defaultOutput, redirect: '/login-pupil' };
      }
      // DEV-794's google door is not here: it runs ahead of the gauntlet
      // (see "A pupil at a school-Google school" above), with the same
      // Skolon and staff exclusions this ordering gives the others.
    }
    if (!schoolSlug && locale === 'en-GB') {
      return { ...defaultOutput, redirect: '/school' };
    }
  }

  if (
    !token &&
    !noRedirectWithoutTokenPaths.includes(ctx.pathname) &&
    isServerRender(ctx)
  ) {
    return { ...defaultOutput, redirect: '/' };
  } else if (token && !noRedirectWithTokenPaths.includes(ctx.pathname)) {
    let verifiedUser;
    try {
      verifiedUser = await verifyUser(token);
    } catch (err) {
      Sentry.captureException(err, {
        tags: { flow: 'login-redirect', status: err.status },
      });
      if (err.status === 401) {
        const cookieOptions = {
          path: '/',
          domain: getCookieDomain(ctx),
          maxAge: -1,
        };
        nookies.set(ctx, 'token', '', cookieOptions);
        nookies.set(ctx, 'user', '', cookieOptions);

        if (isSkolonLogin) {
          // An expired Skolon token has no passwordless account behind it —
          // /?expired=true would drop a tile user on the phone-login form.
          // /login-skolon (no from=app, cookies just cleared) starts SSO
          // again, which re-runs Auth0's fetchUserProfile: expiry is where
          // a revoked Skolon licence is actually caught.
          return { ...defaultOutput, redirect: SKOLON_LOGIN_PATH };
        }
        if (isSchoolSsoLogin) {
          // Same reasoning: no passwordless account behind it. /login-school
          // (no token now) starts SSO again, bounded by its start budget.
          return { ...defaultOutput, redirect: SCHOOL_SSO_LOGIN_PATH };
        }
        const loginPath = preferLoginVariant === 'email' ? '/email' : '/';
        return {
          ...defaultOutput,
          redirect: `${loginPath}?expired=true`,
        };
      } else if (err.status === 400) {
        return { ...defaultOutput, redirect: activationPath };
      } else if (err.status === 404 && canRescueFirstLogin) {
        // First-time UK or federated user: identify's GET /user returns 404
        // when no subscription row exists yet (st-identify
        // subscriptions.js). A teacher (Microsoft login) → activate a
        // PROFESSIONAL voucher; a student whose subscription isn't created
        // yet → same activation surface. WITHOUT this, a 404 falls through
        // to the FI/SE passwordless fallback below and strands the user at
        // /email. Scoped to canRescueFirstLogin so FI/SE passwordless 404
        // behaviour is unchanged.
        if (activationPath !== '/voucher') {
          // Skolon / school SSO: back to silent activation, even from
          // /voucher itself.
          return { ...defaultOutput, redirect: activationPath };
        }
        if (ctx.pathname === '/voucher') {
          // Already on the voucher page — render it (the page needs only
          // host+token, no user object on a 404). Must NOT redirect to
          // /voucher again, or getRedirect re-runs verifyUser → 404 →
          // redirect → infinite loop (the /voucher self-redirect bug).
          return defaultOutput;
        }
        return { ...defaultOutput, redirect: '/voucher' };
      }
      // Unknown verifyUser error (5xx/network): fall through to the default
      // login handling below, as before — except for a school-SSO pupil,
      // for whom "below" is the FI/SE phone form they have no account on.
      // /login-school?from=app re-activates with the token they hold: if
      // identify has recovered it hands off again, otherwise it ends on
      // "Ask your teacher" with a retry. Its from=app budget bounds the
      // round trip if verifyUser keeps failing while activation succeeds.
      if (isSchoolSsoLogin) {
        return { ...defaultOutput, redirect: SCHOOL_SSO_APP_RETURN_PATH };
      }
    }

    if (verifiedUser) {
      if (verifiedUser.status === 'inactive') {
        if (activationPath !== '/voucher') {
          // Skolon / school SSO: contract ended or not yet activated — the
          // login page decides which screen, never the voucher field.
          return { ...defaultOutput, redirect: activationPath };
        }
        if (ctx.pathname === '/voucher') {
          // Already on the voucher page, stay here.
          return { ...defaultOutput, pageProps: { user: verifiedUser } };
        }
        // Redirect to voucher page for inactive users.
        return { ...defaultOutput, redirect: '/voucher' };
      }

      // User is active, redirect to Toosa. Computed OUTSIDE the verifyUser
      // try/catch on purpose: getToosaUrlByLocale is fail-loud (it throws
      // when a TOOSA_URL_* is unconfigured). Inside the catch that throw was
      // silently swallowed, stranding active users on /email — e.g. en-GB
      // if TOOSA_URL_UK ever resolves empty. Out here a misconfig surfaces.
      const redirectPath = getToosaRedirectUrl({
        token,
        user: verifiedUser,
        locale,
        cookieFunctional,
        cookieStatistics,
        keepSignedIn: nookies.get(ctx)[KEEP_SIGNED_IN_COOKIE_NAME] === '1',
      });

      return {
        ...defaultOutput,
        pageProps: { user: verifiedUser, redirectPath },
      };
    }
  }

  // if user logged in with an email previously
  if (preferLoginVariant === 'email' && ctx.pathname === '/') {
    return { ...defaultOutput, redirect: '/email' };
  }

  return defaultOutput;
};

// No shared header (and so no tappable menu into settings and legal text).
//
// /login-school (DEV-794, review 2026-10-26 S3) is a five-year-old's
// sign-in in progress: a spinner, then either toosa or a picture-only
// "Ask your teacher". Its only adult exit is the screens' own "Teacher or
// staff?" link. A pathname, so even the first server render has no header.
//
// /school hides it only while it starts SSO — its picker keeps the
// header — so it cannot be listed here. The server auto-start sets
// `disableHeader` on that branch instead, which lands in the same pageProps;
// a pick made on the page itself (or under ?pick=1) hides it client-side
// through PeikkoContext's `hideHeader` (round 4 S2).
export const pagesWithNoHeader = ['/maintenance', SCHOOL_SSO_LOGIN_PATH];

const getInitialProps = async (Component, ctx) => {
  const defaultOutput = { initialProps: {}, hasError: false };
  if (pagesWithNoHeader.includes(ctx.pathname)) {
    return { ...defaultOutput, initialProps: { disableHeader: true } };
  }

  if (Component.getInitialProps) {
    try {
      return {
        ...defaultOutput,
        initialProps: await Component.getInitialProps(ctx),
      };
    } catch (e) {
      if (process.env.NODE_ENV !== 'production') {
        console.error(e);
      }
      Sentry.captureException(e);
      return { ...defaultOutput, hasError: true };
    }
  }
  return defaultOutput;
};

type InitialLocaleUser = {
  locale?: string;
};

type InitialLocaleQuery = {
  locale?: string;
  country?: string;
};

const getInitialLocale = (
  user: InitialLocaleUser = {},
  query: InitialLocaleQuery = {},
): string => {
  if (user.locale) return user.locale;
  if (query.locale) return query.locale;
  return undefined;
};

const getLoginVariantFromUrl = (url: string): string | null => {
  const path = url.split('?')[0];
  if (path === '/email') return 'email';
  if (path === '/phone') return 'phone';
  return null;
};

type PeikkoAppProps = {
  pageProps: any;
};

class PeikkoApp extends App<PeikkoAppProps> {
  static async getInitialProps({ Component, ctx }): Promise<any> {
    let {
      user,
      token,
      gdprVersion,
      locale: cookieLocale,
      onboarding = '',
      preferLoginVariant,
      gdprConsentGiven,
      [GDPR_FUNCTIONAL_ENABLED]: cookieFunctional = 'missing',
      [GDPR_STATISTICS_ENABLED]: cookieStatistics = 'missing',
      [SCHOOL_COOKIE_NAME]: schoolSlug,
      [SCHOOL_AUTH_MODEL_COOKIE_NAME]: schoolAuthModel,
      [SCHOOL_PUPIL_LOGIN_COOKIE_NAME]: schoolPupilLogin,
    }: any = nookies.get(ctx);
    const host = getHost(ctx);
    const localeFromQueryParams = getInitialLocale(user, ctx.query); // locale from toosa
    const { req } = ctx;
    if (req) {
      const loginVariant = getLoginVariantFromUrl(req.url);
      if (loginVariant) {
        setServerSideCookie(ctx, PREFERRED_LOGIN_VARIANT, loginVariant);
        preferLoginVariant = loginVariant;
      }
    }
    const locale = localeFromQueryParams || cookieLocale;

    const defaultPageProps = {
      token,
      // Resolved from the request UA, not navigator, so the server and client
      // render the same thing. Mirrors how st-toosa decides the same question.
      isNativeShell: isKuoriShell(ctx.req?.headers?.['user-agent'] ?? ''),
      pathname: ctx.pathname,
      host,
      locale,
      cookieFunctional: cookieFunctional === 'true',
      gdprConsentGiven,
      // See componentDidMount's locale write.
      isSchoolGoogleDevice:
        resolvePupilDoor(schoolPupilLogin, schoolAuthModel) === 'google',
    };

    const { initialProps, hasError } = await getInitialProps(Component, ctx);
    const props = {
      pageProps: { ...defaultPageProps, ...initialProps },
      hasError,
    };

    const { pageProps: pagePropsUpdates, redirect: redirectPath } =
      await getRedirect(
        ctx,
        locale,
        gdprVersion,
        token,
        user,
        onboarding,
        preferLoginVariant,
        gdprConsentGiven,
        cookieFunctional,
        cookieStatistics,
        schoolSlug,
        schoolAuthModel,
        schoolPupilLogin,
      );

    return redirectPath
      ? redirect(redirectPath, ctx)
      : { ...props, pageProps: { ...props.pageProps, ...pagePropsUpdates } };
  }

  componentDidMount() {
    const pageProps = get(this.props, 'pageProps', {});
    const { user, locale, isSchoolGoogleDevice } = pageProps;
    void Analytics.setTrackingOptions(user);
    // Set locale cookie if valid (country is already set server-side).
    //
    // 28 days for everyone, as before — except on a school-Google device
    // (review 2026-10-23, S2), where /school and /login-school write it for
    // a year and this rewrite on every mount would quietly shorten it back
    // to 28 days. Scoped to that device rather than moved to 365 for all:
    // the FI/SE lifetime is a separate decision, and the google door no
    // longer depends on the locale cookie anyway (it runs before /country).
    if (locale && allowedLocales.includes(locale)) {
      if (isSchoolGoogleDevice) {
        void presetSchoolSsoCookies([getSchoolSsoLocaleCookie(locale)]);
      } else {
        setLocale(locale);
      }
      Analytics.setLocale(locale);
    }
    this.maybeRedirectToToosa();
  }

  componentDidUpdate(prevProps) {
    // The active-user → Toosa redirect lives in pageProps.redirectPath (set
    // by getRedirect). componentDidMount fires only on the INITIAL App
    // mount, so client-side navigations (router.push) that reach an active
    // state — e.g. the UK roster phrase-change flow — never consumed it,
    // stranding the student on a peikko page (then /email via the index
    // page's preferLoginVariant logic). Re-run the check whenever
    // redirectPath newly appears or changes on update.
    const prev = get(prevProps, 'pageProps.redirectPath');
    const curr = get(this.props, 'pageProps.redirectPath');
    if (curr && curr !== prev) {
      this.maybeRedirectToToosa();
    }
  }

  maybeRedirectToToosa() {
    const { redirectPath, pathname } = get(this.props, 'pageProps', {});
    if (redirectPath && redirectPath !== pathname) {
      redirect(redirectPath);
    }
  }

  render() {
    const { Component, pageProps, router, hasError }: any = this.props;
    const {
      locale,
      disableHeader,
      user,
      token,
      isNativeShell,
      gdprConsentGiven,
    } = pageProps;
    return (
      <>
        <Splash locale={locale} title={getTitleByLocale(locale)} />
        <AppContainer>
          <Head>
            <title>{getTitleByLocale(locale)}</title>
            <meta
              name="viewport"
              content="width=device-width, initial-scale=1.0, viewport-fit=cover"
            />
            <link rel="shortcut icon" href="/favicon-32.png" />
            <link
              rel="preload"
              href="/fonts/Gramatika-Bold.woff2"
              as="font"
              type="font/woff2"
              crossOrigin="anonymous"
            />
          </Head>
          <AvatarProvider>
            <PeikkoProvider locale={pageProps.locale}>
              {/* hideHeader is the client-side switch for the same thing as
                  disableHeader: a page that only knows after rendering —
                  /school's picker starting SSO (round 4 S2) — hides the
                  header through PeikkoContext. */}
              <PeikkoContext.Consumer>
                {({ hideHeader }) =>
                  disableHeader || hideHeader || pageProps.redirectPath ? null : (
                    // On the redirect/loading screen the buddle avatar below
                    // is the focal point — hide the header so its avatar
                    // logo doesn't show a second, near-identical waving
                    // avatar.
                    <Header
                      user={user}
                      token={token}
                      isNativeShell={isNativeShell}
                      gdprConsentGiven={gdprConsentGiven}
                    />
                  )
                }
              </PeikkoContext.Consumer>
              <ComponentContainer role="main">
                <PageTransition timeout={50} classNames="page-transition">
                  {hasError ? (
                    <Error key="error" />
                  ) : pageProps.redirectPath ? (
                    // An active-user → toosa redirect is queued (fired
                    // client-side in componentDidMount/Update). Show the
                    // house buddle avatar instead of mounting the real page,
                    // so the FI/SE passwordless index doesn't flash for a
                    // frame before the cross-origin redirect lands. Same
                    // pattern as login.tsx / logout.tsx.
                    <SpinnerContainer key="redirect">
                      <WelcomingAvatar product={getProduct(user)} />
                    </SpinnerContainer>
                  ) : (
                    <Component {...pageProps} key={router.route} />
                  )}
                </PageTransition>
              </ComponentContainer>
            </PeikkoProvider>
          </AvatarProvider>
        </AppContainer>
      </>
    );
  }
}

export default PeikkoApp;
